import { useQuery } from "@tanstack/react-query"
import { CalendarCheck, CalendarClock, CalendarRange, CircleAlert, Inbox } from "lucide-react"
import { Link } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { CalendarioTareas } from "@/componentes/tareas/CalendarioTareas"
import { FILTROS_VACIOS } from "@/componentes/tareas/FiltrosTareas"
import type { Tarea } from "@/componentes/tareas/TarjetaTarea"
import { Insignia } from "@/componentes/ui/Insignia"
import { TarjetaNumero } from "@/componentes/ui/TarjetaNumero"
import { urgenciaDe } from "@/lib/fechas"

/**
 * La vista principal del módulo de tareas: qué me toca.
 *
 * ⚠ **Esto NO es el panel del sistema.** Ahí van los indicadores operativos y
 * financieros de la agencia —expedientes detenidos, cobranza— y se responde «cómo
 * va el negocio». Aquí se responde «qué hago yo hoy», que es otra pregunta y otro
 * público. Vivieron juntos un rato y el resultado era un panel donde había que leer
 * los seis números para saber cuáles hablaban de uno.
 *
 * ⚠ **El mes va DENTRO de esta vista, no en una pestaña propia.** A pantalla
 * completa, un calendario de treinta y cinco casillas ocupa todo para decir lo que
 * dice —qué días están cargados—, y obliga a cambiar de pestaña para cruzarlo con
 * los contadores, que es justo lo que uno quiere hacer. Compartiendo la vista entra
 * en compacto: dos fichas por día en vez de tres, y el «+N más» para el resto.
 *
 * ⚠ **Todo aquí es MÍO.** El calendario va con `soloMias` fijo y sin barra de
 * filtros: el resumen no se recorta. Para mirar el trabajo de otros están el tablero,
 * la lista y la carga, cada uno con sus filtros.
 *
 * ⚠ **Un cero se enseña; un `null` no.** Cero es «no hay nada pendiente», que es una
 * noticia buena. `null` es «no te toca» —falta el permiso— y entonces la tarjeta
 * desaparece.
 */

interface Panel {
  vencidas: number
  para_hoy: number
  esta_semana: number
  proximas: Tarea[]
  sin_asignar: number | null
}

/** El mes de esta vista no se filtra: es el mismo recorte que los contadores. */
const MIS_ABIERTAS = { ...FILTROS_VACIOS, soloMias: true, soloAbiertas: true }

type Props = {
  usuarioId: number | undefined
  onAbrir: (id: number) => void
  seleccionada: number | null
}

export function ResumenTareas({ usuarioId, onAbrir, seleccionada }: Props) {
  const consulta = useQuery({
    queryKey: ["panel"],
    queryFn: () => api.get<Panel>("/api/v1/panel"),
    // El mismo sondeo que la bandeja: esta gente deja la pantalla abierta.
    refetchInterval: 60_000,
  })

  if (consulta.isPending) {
    return (
      <p role="status" className="py-16 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }

  if (consulta.isError || !consulta.data) {
    return (
      <p role="alert" className="py-16 text-center text-sm" style={{ color: "var(--destructive)" }}>
        {consulta.error instanceof ErrorApi
          ? consulta.error.message
          : "No se pudo cargar el resumen"}
      </p>
    )
  }

  const p = consulta.data

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TarjetaNumero
          valor={p.vencidas}
          etiqueta="vencidas"
          a="/tareas?vista=lista&mias=1&abiertas=1"
          icono={<CircleAlert size={16} aria-hidden={true} />}
          alarma
          prueba="panel-vencidas"
        />
        <TarjetaNumero
          valor={p.para_hoy}
          etiqueta="vencen hoy"
          a="/tareas?vista=lista&mias=1&abiertas=1"
          icono={<CalendarClock size={16} aria-hidden={true} />}
          prueba="panel-hoy"
        />
        <TarjetaNumero
          valor={p.esta_semana}
          etiqueta="esta semana"
          a="/tareas?vista=lista&mias=1&abiertas=1"
          icono={<CalendarRange size={16} aria-hidden={true} />}
          prueba="panel-semana"
        />
        {/* Sin dueño SÍ es de tareas —a diferencia de los expedientes detenidos o la
            cobranza— y por eso se queda aquí: es trabajo que hay que repartir, que
            es de lo que va esta pantalla. */}
        {p.sin_asignar !== null && (
          <TarjetaNumero
            valor={p.sin_asignar}
            etiqueta="sin dueño"
            a="/tareas?vista=lista&sin=1&abiertas=1"
            icono={<Inbox size={16} aria-hidden={true} />}
            prueba="panel-sin-asignar"
          />
        )}
      </div>

      {/* Dos tercios para el mes y uno para la lista: el calendario necesita ancho
          —siete columnas— y la lista necesita alto. Al revés, las casillas quedan
          tan estrechas que el título de una tarea no se lee y el mes deja de servir.
          Por debajo de `xl` se apilan: en pantalla estrecha, dos columnas de esto no
          caben sin romper las dos. */}
      <div className="grid gap-4 xl:grid-cols-3">
        <section className="xl:col-span-2">
          <h2 className="rotulo mb-3">Tu mes</h2>
          <CalendarioTareas
            filtros={MIS_ABIERTAS}
            usuarioId={usuarioId}
            onAbrir={onAbrir}
            seleccionada={seleccionada}
            compacto
          />
        </section>

        <section>
          <h2 className="rotulo mb-3">Tareas próximas a vencer</h2>
          {p.proximas.length === 0 ? (
            <div
              className="flex flex-col items-center gap-3 superficie py-12"
              style={{ color: "var(--fg-muted)" }}
            >
              <CalendarCheck size={24} aria-hidden={true} />
              <p className="text-sm">No tienes tareas abiertas.</p>
            </div>
          ) : (
            <ul className="superficie overflow-hidden">
              {p.proximas.map((t, i) => {
                const urgencia = urgenciaDe(t.vence_en)
                return (
                  <li
                    key={t.id}
                    // El separador va entre filas y no debajo de cada una: con una
                    // regla al final, la última queda con una línea suelta contra el
                    // borde de la superficie y se lee como una fila vacía.
                    style={i === 0 ? undefined : { borderTop: "1px solid var(--cristal-filo)" }}
                  >
                    {/* Al detalle directamente, no a la bandeja: esta vista existe
                        para ahorrar el paso de buscarla. */}
                    <Link
                      to={`/tareas?tarea=${t.id}`}
                      data-testid={`panel-tarea-${t.id}`}
                      className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm toque-cristal"
                    >
                      <span className="min-w-0">
                        <span
                          className="block font-mono text-xs tabular-nums"
                          style={{ color: "var(--fg-muted)" }}
                        >
                          {t.codigo}
                        </span>
                        <span className="block truncate">{t.titulo}</span>
                      </span>
                      <Insignia tono={urgencia.tono} className="shrink-0 tabular-nums">
                        {urgencia.texto}
                      </Insignia>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}
